/**
 * **S3Session —— 对局级的同步层。**
 *
 * ## 生命周期：随**对局**
 *
 * ```
 * 开局 → hostSession() / clientSession() ──────► 结束 → close()（自动）
 * ```
 *
 * `S3Peer` 活得比它长（随房间），所以**开下一局不需要重建 WebRTC 连接**。
 * 反过来，它自己也**不许**碰房间级的东西（名册共识、游戏规则、准备状态）——
 * 那些属于 `S3Peer` 和大厅。
 *
 * ## 它不知道自己在同步什么游戏
 *
 * 全部游戏知识都在 `S3Rules` 里（`docs/netcode-s3.md` §4）：
 * 这个文件里没有"牌""手牌""回合"这些词，只有
 * `State / View / Action / Event` 四个类型参数。唯一的约定是
 * **玩家 id 就是 peer id**（字符串）—— 因为对端的身份本来就是这个。
 *
 * ## 事件式：房主推，客户端从不问
 *
 * ```
 * 客户端 send(action)
 *    └─► 房主 reduce() → 对**每个玩家各算一份**新视图
 *          ├─ 每人一条 …/view（增量 + 视图哈希）
 *          ├─ 广播 …/event（只给动画/音效，不参与状态）
 *          └─ 回一个"收到/不行"
 * ```
 *
 * `resync` 是唯一的兜底，且只在**哈希对不上**或**换了连接**时才走。
 * 常规路径上客户端一句都不问。
 */

import { z } from 'zod';
import { t } from '../i18n';
import { AccessDeniedError, S3Error } from './protocol';
import { applyViewPatches, diffView, PatchListSchema, type Patch } from './patch';
import { hashValue } from './hash';
import type { PeerLiveness } from './heartbeat';
import type { S3Channel } from './peer';
import type { PardonReport } from './rpc';

// ============================================================
// 规则适配器 —— `net/` 与"某一款游戏"之间**唯一**的接缝
// ============================================================

/** 开局上下文。交给游戏的"建初始状态"，怎么用由它决定 */
export interface S3StartContext {
  readonly sessionId: string;
  readonly host: string;
  readonly players: readonly string[];
  readonly options: unknown;
}

/** 规则层的裁决。非法动作返回 `{ok:false, reason}`，**不抛异常** */
export type S3Verdict<State, Event> =
  | { readonly ok: true; readonly state: State; readonly events: readonly Event[] }
  | { readonly ok: false; readonly reason: string };

/**
 * 房主在 `prepare` 里能做的事。
 *
 * 刻意**只有请求**：能发通知的话，规则层就会开始偷偷推状态，
 * 而权威视图只有一个出口（`pushViews`）。
 */
export interface HostIo {
  request<T>(to: string, method: string, payload: unknown, schema: z.ZodType<T>, timeoutMs?: number): Promise<T>;
}

export interface S3ActionMeta {
  /**
   * **这个动作是谁发的**，由网络层认定，不是动作自己声称的。
   *
   * ⚠️ 规则层必须拿它去校验动作里的 `actor` 字段。本项目明确不做反作弊，
   * 但"不信任客户端自称的身份"是**鲁棒性**问题：一条伪造的
   * `actor` 会让 reducer 以别人的名义结算，而那是查不出来的那种 bug。
   */
  readonly actor: string;
}

export interface S3Rules<State, View, Action, Event> {
  /** 开局：房主调**一次**，产出完整初始状态（发牌、种子、一切） */
  createInitialState(ctx: S3StartContext): State;
  /** 脱敏：完整状态 → 某个玩家能看到的视图。纯函数 */
  project(state: State, viewer: string): View;
  /** 规则：应用一个意图 */
  reduce(state: State, action: Action, meta: S3ActionMeta): S3Verdict<State, Event>;
  /** 结束了吗。`true` 之后 Session 自己收摊 */
  isOver(state: State): boolean;

  /**
   * **房主在应用一个动作之前，可以补一次网络往返。**
   *
   * 评审那套抽牌交互需要的：抽牌人发来一个 index，但"第 index 位是哪张牌"
   * 只有**被抽的人自己知道**（手牌顺序是本机的事，平时不同步）。
   * 所以房主必须在结算前问一次。
   *
   * 返回一个新的动作（通常是原动作补上一点信息），或原样返回。
   * ⚠️ **只有房主会调它**，而且是在 `reduce` 之前 —— 于是
   * "问"这件事对规则层是显式的，不是藏在网络层里的魔法。
   */
  prepare?(state: State, action: Action, io: HostIo): Promise<Action> | Action;

  /**
   * **房主侧的时序策略。**
   *
   * 评审与 `architecture.md` §3.4 的硬约束：`core/` 里**不许有时间**，
   * 所以"什么时候关窗"必须由房主决定，而这个决定本身也是一个动作
   * （`window/timeout`）。这个钩子就是那个决定的出处。
   *
   * 返回 `null` = 现在不需要定时器。返回 `{ afterMs, action }` =
   * `afterMs` 之后由房主发出 `action`。
   *
   * ⚠️ 每次状态变化都会重新问一遍，所以它可以随局面改主意
   * （"只在窗口开着时才要定时器"）。
   */
  planTimeout?(state: State, host: string): { readonly afterMs: number; readonly action: Action } | null;

  /**
   * **房主代打机器人。**（可选）返回 `null` = 现在要动的人不是机器人。
   * Session 每次状态变化先问它，再退回 `planTimeout`。
   */
  botAction?(state: State): { readonly afterMs: number; readonly action: Action } | null;

  /**
   * **房主：把刚发生的事件交给规则层。**（可选）
   *
   * 远程 MCP 机器人用它实现"别人行动也唤醒 LLM"。事件是**公开的**
   * （`core/events.ts` 明文规定不得携带隐藏信息）。传的是**已结算的动作**
   * 本身，规则层据此取动作自称的 `actor` —— 房主代打的机器人动作也能被识别。
   */
  observe?(state: State, action: Action, events: readonly Event[]): void;

  /**
   * **把"某个玩家在线/离线"这个网络事实变成一条规则动作。**（可选；只有房主会调）
   *
   * Session 在 peer 加入 / 离开 / 心跳判死时调它，然后用 `applyAction` 发出去 ——
   * 规则层据此把 `net/connected` / `net/disconnected` 落进状态。
   */
  presenceAction?(player: string, present: boolean): Action | null;

  readonly schemas: {
    readonly view: z.ZodType<View>;
    readonly action: z.ZodType<Action>;
    readonly event: z.ZodType<Event>;
  };
}

// ============================================================
// 对外形状
// ============================================================

export type SessionStatus = 'opening' | 'live' | 'suspended' | 'closed';

/** 业务裁决。协议/传输故障**抛异常**，不走这里 */
export type S3Ack = { readonly ok: true } | { readonly ok: false; readonly reason: string };

export interface S3SessionStats {
  /** 房主推出去的视图批次数 */
  readonly viewsPushed: number;
  /**
   * 客户端**回退全量**的次数（哈希对不上、增量应用不了……）。
   *
   * ⚠️ 它是这一层最灵敏的健康指标：**稳定网络里它应当恒为 0**。
   * 开局那一次全量**不算**在这里（那是设计如此，见 `initialSyncs`）。
   */
  readonly resyncs: number;
  /** 认下开局之后主动要的那一次全量。正常情况恒为 1 */
  readonly initialSyncs: number;
  /** 房主应用过的动作数 */
  readonly actionsApplied: number;
}

/** 房主侧的一次动作流水（诊断用）。成功与拒绝都留一条，新的在后面 */
export interface S3ActionJournalEntry {
  /** 记下来的时刻（Date.now） */
  readonly at: number;
  /** 网络层认定的发起者（不是动作自称的 actor） */
  readonly actor: string;
  /** 收到的原始动作 */
  readonly action: unknown;
  /** prepare 补过信息之后、真正丢给 reduce 的那一份 */
  readonly prepared: unknown;
  readonly ok: boolean;
  /** 被拒的原因（ok:false） */
  readonly reason?: string;
  /** 应用成功时，规则层产出的事件类型列表（只要 t，够诊断用） */
  readonly events?: readonly string[];
}

/** 房主现在排着的那个定时器（关窗 / 替掉线的人决定 / 机器人代打） */
export interface S3PendingTimeout {
  readonly afterMs: number;
  readonly action: unknown;
  readonly scheduledAt: number;
  /** 排它的是机器人代打还是时序策略 */
  readonly source: 'bot' | 'timeout';
}

/**
 * **一局 Session 的现场读数。**
 *
 * 只做观察：debug() 是一次调用就现取一份，不持有状态、不改任何东西。
 * 房主那侧的 state 是**权威状态的原样引用**（联机客户端恒为 null）——
 * 这是排查“动作发了、画面不对”那类问题的唯一入口：光看客户端视图看不出
 * 窗口的 committed、手牌到底删没删、options 里还剩什么。
 */
export interface S3SessionDebug {
  readonly id: string;
  readonly role: 'host' | 'client';
  readonly selfId: string;
  readonly status: SessionStatus;
  readonly host: string | null;
  readonly players: readonly string[];
  readonly bots: readonly string[];
  readonly stats: S3SessionStats;
  /** 房主：权威状态原样。客户端恒为 null */
  readonly state: unknown;
  /** 房主：推视图的序号；客户端：最后认下的序号 */
  readonly seq: number;
  /** 每个玩家最近一次推过去的视图哈希 */
  readonly viewHashes: Readonly<Record<string, string>>;
  /** 动作流水（最近 N 条，新的在后） */
  readonly journal: readonly S3ActionJournalEntry[];
  /** 现在排着的定时器 */
  readonly pendingTimeout: S3PendingTimeout | null;
  /** 计时器在不在跑 */
  readonly timers: { readonly host: boolean; readonly hostGrace: boolean };
}

export interface S3Session<View, Action, Event> {
  readonly id: string;
  readonly isHost: boolean;
  readonly selfId: string;
  status(): SessionStatus;
  host(): string | null;
  players(): readonly string[];
  /** 最后一次已知的视图。**`close()` 之后仍然可读** —— 终局画面不该被清掉 */
  view(): View | null;
  stats(): S3SessionStats;

  /** 发一个意图。房主本地应用；客户端发给房主 */
  send(action: Action): Promise<S3Ack>;

  /**
   * **手动同步。**（齿轮菜单里的「刷新同步」）
   *
   * - 客户端：向房主再要一份**全量视图**，丢掉本地可能已经落后的副本；
   * - 房主：重新按每个人投影并推全量。
   *
   * 不重置对局、不解散房间 —— 只是把数据对齐，用来从「卡住」里脱身。
   * 这是玩家按的正常路径，**不计入 `resyncs`**（那一条是 bug 信号的计数）。
   */
  sync(): Promise<void>;

  onView(handler: (view: View) => void): () => void;
  onEvent(handler: (events: readonly Event[]) => void): () => void;
  onStatus(handler: (status: SessionStatus, reason?: string) => void): () => void;
  onError(handler: (error: S3Error) => void): () => void;
  /** 名册变化。房主据此决定要不要发 `net/disconnected` 之类的动作 */
  onPresence(handler: (roster: readonly string[], present: readonly string[]) => void): () => void;

  /**
   * **客户端：应答房主的询问。**
   *
   * 房主在结算前可能要问本人一件事（比如两张同名："你现在的最终手牌顺序"）。
   * 那是**只有本人知道**的信息 —— 联机时手牌顺序平时一个字节都不同步。
   *
   * ⚠️ 方法名会被加上**会话前缀**，`close()` 时整段注销，
   * 所以上一局注册的应答不会在新一局里被问到。
   */
  onAsk<T>(
    method: string,
    schema: z.ZodType<T>,
    handler: (payload: T, from: string) => unknown | Promise<unknown>,
  ): () => void;

  /**
   * **房主：朝某个 peer 发一个请求。**
   *
   * 只在 `prepare` 里用得到（`HostIo.request` 就是这个）。它是一条普通的
   * 请求/响应 —— 带超时、带校验、失败会抛 —— 因为它承载的是**会影响
   * 结算结果**的数据，不能像旁路通道那样"丢了就算"。
   */
  ask<T>(to: string, method: string, payload: unknown, schema: z.ZodType<T>, timeoutMs?: number): Promise<T>;

  /** 收摊。幂等；**不会碰 `S3Channel`**（那属于房间） */
  close(reason?: string): void;

  /**
   * **一局 Session 的现场读数。**（诊断用，只读）
   *
   * 房主那侧带回**完整的权威状态**与动作流水，客户端只带回协议层的状态。
   * 见 S3SessionDebug。
   */
  debug(): S3SessionDebug;
}

export interface HostSessionOptions {
  readonly players: readonly string[];
  readonly options?: unknown;
  /** 不指定就自己生成一个 */
  readonly sessionId?: string;
  /** 机器人座位：没有 peer，**恒当在线**，由 `rules.botAction` 代打 */
  readonly bots?: readonly string[];
}

export interface ClientSessionOptions {
  /**
   * 期望的会话 id。
   *
   * - 给了：只接受**这个** id 的开局，其余一律忽略（挡住上一局的残响）
   * - 没给（`null`）：接受找到的**第一个**开着的会话
   *
   * 客户端建得比房主 `open` 早或晚都行 —— 两条路都铺了：
   * 早建的靠推送，晚建的靠一次性的探测（见 `ProbeReplySchema`）。
   */
  readonly sessionId?: string | null;
  /** 房主掉线后等多久收摊。默认 60 秒（Q11：不迁移房主，只等重连） */
  readonly hostGraceMs?: number;
}

// ============================================================
// 协议
// ============================================================

/** 开局宣告是**唯一一个不带会话前缀**的方法 —— 不然客户端没法知道 id */
export const SESSION_OPEN_METHOD = 's3/session/open';

/** 探测有没有开着的会话。和 `open` 一样不带前缀：问的人还不知道 id */
export const SESSION_PROBE_METHOD = 's3/session/probe';

const DEFAULT_HOST_GRACE_MS = 60_000;

const OpenPayloadSchema = z.object({
  sessionId: z.string().min(1),
  host: z.string().min(1),
  players: z.array(z.string()),
  options: z.unknown(),
});

const ViewPayloadSchema = z.object({
  seq: z.number().int().nonnegative(),
  hash: z.string(),
  /** 全量视图。有它就忽略 `patches` */
  view: z.unknown().optional(),
  patches: PatchListSchema.optional(),
});

const ResyncPayloadSchema = z.object({
  seq: z.number().int().nonnegative(),
  hash: z.string(),
  view: z.unknown(),
});

const ClosePayloadSchema = z.object({ reason: z.string().optional() });

/**
 * 探测应答。
 *
 * ⚠️ 为什么除了 `open` 推送之外还需要一个**探测**：推送有两种错过方式 ——
 * 客户端建得比 `open` 晚（消息早过去了），或者客户端和房主几乎同时建
 * （谁先谁后不确定）。而"错过"的表现是客户端**永远停在 `opening`**，
 * 屏幕上什么都没发生，也没有报错。这在真浏览器里被抓到过一次。
 *
 * 于是：**推一次 + 问一次**。客户端建好之后除了挂 `open` 的监听，
 * 还朝每个 peer 问一句"这一局开着吗"。两条路任意一条通就够了，
 * 而且都不是轮询 —— 问只发生一次。
 */
const ProbeReplySchema = z.union([
  z.object({
    open: z.literal(true),
    sessionId: z.string(),
    host: z.string(),
    players: z.array(z.string()),
    options: z.unknown(),
  }),
  z.object({ open: z.literal(false) }),
]);

const AckSchema = z.union([
  z.object({ ok: z.literal(true) }),
  z.object({ ok: z.literal(false), reason: z.string() }),
]);

let sessionCounter = 0;

export function makeSessionId(): string {
  sessionCounter += 1;
  return `${Date.now().toString(36)}${sessionCounter.toString(36)}${Math.floor(Math.random() * 0xffff).toString(36)}`;
}

// ============================================================
// 实现
// ============================================================

interface CommonInit<State, View, Action, Event> {
  readonly channel: S3Channel;
  readonly rules: S3Rules<State, View, Action, Event>;
}

function createSession<State, View, Action, Event>(
  init: CommonInit<State, View, Action, Event> & {
    readonly role: 'host' | 'client';
    readonly sessionId: string;
    readonly expectedSessionId: string | null;
    readonly players: readonly string[];
    readonly options: unknown;
    readonly hostGraceMs: number;
    readonly bots?: readonly string[];
  },
): S3Session<View, Action, Event> {
  const { channel, rules, role } = init;
  const selfId = channel.selfId;

  // ---- 会话身份。客户端在收到 `open` 之前是空的 ----
  let sessionId = init.sessionId;
  let hostId: string | null = role === 'host' ? selfId : null;
  let roster: readonly string[] = role === 'host' ? [...init.players] : [];
  /** 机器人座位：没有 peer，恒当在线 */
  const botSet = new Set(init.bots ?? []);
  const ns = (): string => `s3/session/${sessionId}`;

  // ---- 共享状态 ----
  let status: SessionStatus = 'opening';
  let currentView: View | null = null;
  let hostGraceTimer: ReturnType<typeof setTimeout> | null = null;
  /** 房主侧的时序策略定时器（关窗 / 替掉线的人做决定）。**只有一个** */
  let hostTimer: ReturnType<typeof setTimeout> | null = null;
  const stats = { viewsPushed: 0, resyncs: 0, initialSyncs: 0, actionsApplied: 0 };

  // ---- 诊断：动作流水 + 现在排着的定时器（只读，见 debug()） ----
  const journal: S3ActionJournalEntry[] = [];
  const JOURNAL_MAX = 40;
  let pendingTimeout: S3PendingTimeout | null = null;

  const pushJournal = (entry: S3ActionJournalEntry): void => {
    journal.push(entry);
    if (journal.length > JOURNAL_MAX) journal.splice(0, journal.length - JOURNAL_MAX);
  };

  // ---- 房主侧 ----
  let state: State | null = null;
  const perPlayerViews = new Map<string, View>();
  let seq = 0;

  // ---- 客户端侧 ----
  let lastSeq = -1;

  // ---- 订阅者 ----
  const viewListeners = new Set<(view: View) => void>();
  const eventListeners = new Set<(events: readonly Event[]) => void>();
  const statusListeners = new Set<(status: SessionStatus, reason?: string) => void>();
  const errorListeners = new Set<(error: S3Error) => void>();
  const presenceListeners = new Set<(roster: readonly string[], present: readonly string[]) => void>();
  const teardown: (() => void)[] = [];

  const track = (off: () => void): void => {
    teardown.push(off);
  };

  /**
   * 在"还不知道会话 id"的时候注册的应答。
   * 客户端认下会话之后由 `adopt` 补注册 —— 见 `onAsk` 的长度说明。
   */
  const queuedAsks: {
    method: string;
    schema: z.ZodType;
    handler: (payload: unknown, from: string) => unknown;
  }[] = [];

  // ============================================================
  // 小工具
  // ============================================================

  /**
   * 调用订阅者，**不让它的异常冒到协议里**。
   *
   * 订阅者是渲染层、音效层、界面。它们抛异常是它们的事：
   * 一次画不出来的动画不该让**别人的出牌请求失败**。
   * 这是"网络层和表现层之间那道墙"在代码上最实在的一处体现。
   */
  const notifySafely = <T,>(listeners: Iterable<(value: T) => void>, value: T, what: string): void => {
    for (const listener of listeners) {
      try {
        listener(value);
      } catch (error) {
        console.error(`[s3] ${t('host.session.subscriberError', { what })}`, error);
      }
    }
  };

  const setStatus = (next: SessionStatus, reason?: string): void => {
    if (status === next) return;
    status = next;
    for (const listener of statusListeners) {
      try {
        listener(next, reason);
      } catch (error) {
        console.error(`[s3] ${t('host.session.statusSubscriberError')}`, error);
      }
    }
  };

  const report = (error: S3Error): void => {
    if (errorListeners.size === 0) console.error('[s3] session:', error);
    for (const listener of errorListeners) listener(error);
  };

  const setView = (view: View): void => {
    currentView = view;
    notifySafely(viewListeners, view, t('host.session.viewLabel'));
  };

  const emitEvents = (events: readonly Event[]): void => {
    if (events.length === 0) return;
    notifySafely(eventListeners, events, t('host.session.eventLabel'));
  };

  const emitPresence = (): void => {
    if (presenceListeners.size === 0) return;
    const online = new Set(channel.peers());
    const present = roster.filter((player) => player === selfId || online.has(player) || botSet.has(player));
    for (const listener of presenceListeners) {
      try {
        listener(roster, present);
      } catch (error) {
        console.error(`[s3] ${t('host.session.rosterSubscriberError')}`, error);
      }
    }
  };

  const asS3Error = (error: unknown): S3Error => {
    if (error instanceof S3Error) return error;
    if (error instanceof Error) return new S3Error('Disconnected', error.message, error);
    return new S3Error('Disconnected', String(error), error);
  };

  function close(reason?: string): void {
    if (status === 'closed') return;
    disarmHostGrace();
    if (hostTimer !== null) {
      clearTimeout(hostTimer);
      hostTimer = null;
    }
    pendingTimeout = null;
    for (const off of teardown.splice(0)) off();
    setStatus('closed', reason);
  }

  /**
   * **把当前现场打一份出来。** 只读；每次调用现取。
   *
   * 房主：带完整 state、每个人最近推过的视图哈希、动作流水、排着的定时器。
   * 客户端：协议层状态 + 最后认下的序号（没有 state）。
   */
  function debug(): S3SessionDebug {
    const viewHashes: Record<string, string> = {};
    for (const [player, view] of perPlayerViews) viewHashes[player] = hashValue(view);
    return {
      id: sessionId,
      role,
      selfId,
      status,
      host: hostId,
      players: [...roster],
      bots: [...botSet],
      stats: { ...stats },
      state: role === 'host' ? state : null,
      seq: role === 'host' ? seq : lastSeq,
      viewHashes,
      journal: journal.map((entry) => ({ ...entry })),
      pendingTimeout: pendingTimeout === null ? null : { ...pendingTimeout },
      timers: { host: hostTimer !== null, hostGrace: hostGraceTimer !== null },
    };
  }

  // ============================================================
  // 房主：推送
  // ============================================================

  const sendView = (player: string, payload: unknown): void => {
    // `notify` 是"发了就不管"。丢了由 `resync` 兜底 —— 为一条视图建立
    // 重传队列，只是在重复 SCTP 的工作（`docs/archive/netcode-s3-evaluation.md` §4）
    channel.notify(`${ns()}/view`, payload, player);
  };

  const projectFor = (player: string): View => {
    if (state === null) throw new Error(t('host.session.projectBeforeStart'));
    return rules.project(state, player);
  };

  /**
   * 把最新的完整状态按人切好推出去。
   *
   * ⚠️ **这里不负责收摊** —— 结束由 `applyAction` 在所有消息发完之后处理，
   * 因为"先把终局视图推完、再宣告收摊"这条顺序是靠可靠有序的信道保证的
   * （`docs/netcode-s3.md` §6.1）。提前在这儿 close 会把同一次的
   * `event` 也一起挡掉。
   */
  const pushViews = (forceFull: boolean): void => {
    if (role !== 'host' || state === null) return;
    seq += 1;
    const currentSeq = seq;

    for (const player of roster) {
      const next = projectFor(player);
      const previous = perPlayerViews.get(player);
      perPlayerViews.set(player, next);

      if (player === selfId) {
        setView(next);
        continue;
      }

      const hash = hashValue(next);
      if (previous === undefined || forceFull) {
        sendView(player, { seq: currentSeq, hash, view: next });
        continue;
      }
      const patches = diffView(previous, next);
      // 视图没变就一个字节都不发。这是"事件式"的直接好处：
      // 没人关心的时候，网络是安静的
      if (patches.length === 0) continue;
      sendView(player, { seq: currentSeq, hash, patches });
    }
    stats.viewsPushed += 1;
  };

  const finish = (reason: string): void => {
    if (status === 'closed') return;
    // 先把"收摊"告诉别人，再拆自己的监听 —— 否则拆完就发不出去了
    channel.notify(`${ns()}/close`, { reason });
    close(reason);
  };

  // ============================================================
  // 房主：应用动作
  // ============================================================

  const applyAction = async (action: Action, actor: string): Promise<S3Ack> => {
    if (role !== 'host' || state === null) return { ok: false, reason: t('host.session.notHost') };

    // ★ 结算前可以补一次网络往返 —— 比如两张同名要问被抽的人要"最终顺序"
    let prepared: Action = action;
    if (rules.prepare !== undefined) {
      try {
        prepared = await rules.prepare(state, action, {
          request: (to, method, payload, schema, timeoutMs) =>
            channel.request(to, `${ns()}/${method}`, payload, schema, timeoutMs),
        });
      } catch (error) {
        return { ok: false, reason: t('host.session.prepareFailed', { reason: asS3Error(error).message }) };
      }
    }

    const verdict = rules.reduce(state, prepared, { actor });
    if (!verdict.ok) {
      pushJournal({ at: Date.now(), actor, action, prepared, ok: false, reason: verdict.reason });
      return { ok: false, reason: verdict.reason };
    }

    state = verdict.state;
    pushJournal({
      at: Date.now(),
      actor,
      action,
      prepared,
      ok: true,
      events: verdict.events.map((event) => String((event as { t?: unknown }).t)),
    });
    stats.actionsApplied += 1;
    // 顺序：视图 → 事件 → 收摊。三者都在同一条可靠有序的信道上，
    // 所以客户端看到的顺序与这里完全一致
    pushViews(false);
    if (verdict.events.length > 0) {
      // ⚠️ 事件**只给动画/音效，不参与状态**（`docs/netcode-s3.md` §5）。
      // 就算整条丢掉，也只是动画不好看，状态不会分叉 ——
      // 所以它走通知通道，而不是给每个 peer 建一个请求
      channel.notify(`${ns()}/event`, { events: verdict.events });
      emitEvents(verdict.events);
      rules.observe?.(state, prepared, verdict.events);
    }
    armHostTimer();
    if (rules.isOver(state)) finish('game/finished');
    return { ok: true };
  };

  /**
   * **把某个 peer 的在线状态落成规则动作。** 只有房主调。
   *
   * `applyAction(action, selfId)` 里 `meta.actor` = 房主；规则层允许房主发
   * `net/connected` / `net/disconnected`（见 `host/rules.ts`）。
   */
  const applyPresence = async (id: string, present: boolean): Promise<void> => {
    if (role !== 'host' || state === null) return;
    if (!roster.includes(id)) return;
    const action = rules.presenceAction?.(id, present) ?? null;
    if (action === null) return;
    await applyAction(action, selfId);
  };

  /**
   * 房主侧的定时器：**每次状态变化重新问一遍规则层**。
   *
   * ⚠️ 旧的定时器一定先清掉 —— 不清的话，一次"关窗"之后
   * 上一个窗口排的那个 timeout 还会打过来，而那时候可能已经开了一个新窗口，
   * 于是它会把**新的**那个窗口关掉。这是这一层最容易出的 bug
   */
  function armHostTimer(): void {
    if (role !== 'host') return;
    if (hostTimer !== null) {
      clearTimeout(hostTimer);
      hostTimer = null;
    }
    pendingTimeout = null;
    if (state === null) return;
    // ★ 先问机器人（要快），再退回时序策略
    const botPlan = rules.botAction?.(state) ?? null;
    const plan = botPlan ?? rules.planTimeout?.(state, selfId) ?? null;
    if (plan === null) return;
    pendingTimeout = {
      afterMs: plan.afterMs,
      action: plan.action,
      scheduledAt: Date.now(),
      source: botPlan !== null ? 'bot' : 'timeout',
    };
    hostTimer = setTimeout(() => {
      hostTimer = null;
      pendingTimeout = null;
      // 定时器到点时局面可能已经变了 —— 再问**同一个来源**一遍，只有仍然要发才发。
      // ⚠️ 早先这里无条件只问 `planTimeout`，于是 `botAction` 排下的定时器到点后
      //    什么也不发，机器人永远停在原地。回归测试见 test/host/bot-action.test.ts。
      if (state === null) return;
      const again =
        botPlan !== null
          ? (rules.botAction?.(state) ?? null)
          : (rules.planTimeout?.(state, selfId) ?? null);
      if (again === null) return;
      void applyAction(again.action, selfId);
    }, plan.afterMs);
  }

  // ============================================================
  // 客户端：回退全量
  // ============================================================

  /**
   * 要一份全量视图。
   *
   * `fallback` 区分两种截然不同的原因，因为它们该被分开计数：
   *
   * - `false`：**开局**（或重连）之后主动要一次。设计如此，正常路径
   * - `true`：**回退**。哈希对不上、增量应用不了 —— 这是 bug 的信号
   */
  /** 真正去要一份全量并应用。**不计数** —— 计数由调用方按原因分开算 */
  const pullFullView = async (): Promise<void> => {
    if (role === 'host' || hostId === null) return;
    const payload = await channel.request(hostId, `${ns()}/resync`, {}, ResyncPayloadSchema);
    const parsed = rules.schemas.view.safeParse(payload.view);
    if (!parsed.success) {
      report(new S3Error('BadResponse', t('host.session.resyncBadView'), z.treeifyError(parsed.error)));
      return;
    }
    if (hashValue(parsed.data) !== payload.hash) {
      report(new S3Error('BadResponse', t('host.session.resyncHashMismatch')));
      return;
    }
    lastSeq = payload.seq;
    setView(parsed.data);
    disarmHostGrace();
    if (status === 'opening' || status === 'suspended') setStatus('live');
  };

  const resync = async (fallback: boolean): Promise<void> => {
    if (role === 'host' || hostId === null) return;
    if (fallback) stats.resyncs += 1;
    else stats.initialSyncs += 1;
    await pullFullView();
  };

  const onClientView = (payload: { seq: number; hash: string; view?: unknown; patches?: Patch[] }): void => {
    if (role === 'host') return;
    // 可靠有序的信道不会乱序，但**重复**是可能的（重连后的重放），
    // 而重复应用一条增量会让视图错得很隐蔽，所以挡一下
    if (payload.seq <= lastSeq) return;

    let candidate: View;
    if (payload.view !== undefined) {
      const parsed = rules.schemas.view.safeParse(payload.view);
      if (!parsed.success) {
        report(new S3Error('BadPayload', t('host.session.fullViewBad'), z.treeifyError(parsed.error)));
        return;
      }
      candidate = parsed.data;
    } else {
      if (currentView === null || payload.patches === undefined) {
        void resync(true).catch((error: unknown) => report(asS3Error(error)));
        return;
      }
      try {
        candidate = applyViewPatches(currentView, payload.patches);
      } catch (error) {
        report(new S3Error('BadPayload', t('host.session.patchApplyFailed'), error));
        void resync(true).catch((e: unknown) => report(asS3Error(e)));
        return;
      }
    }

    if (hashValue(candidate) !== payload.hash) {
      // ★ 协议里最要紧的一条断言：客户端手上只有**视图**，
      //   所以它只能校验视图的哈希（`docs/netcode-s3.md` §6）
      report(new S3Error('BadPayload', t('host.session.viewHashMismatch', { seq: payload.seq })));
      void resync(true).catch((error: unknown) => report(asS3Error(error)));
      return;
    }

    lastSeq = payload.seq;
    setView(candidate);
  };

  // ============================================================
  // 客户端：房主掉线 = 暂停等重连（Q11：不迁移）
  // ============================================================

  function armHostGrace(): void {
    if (hostGraceTimer !== null) return;
    hostGraceTimer = setTimeout(() => {
      hostGraceTimer = null;
      close(t('host.session.hostLeftRoom'));
    }, init.hostGraceMs);
  }

  function disarmHostGrace(): void {
    if (hostGraceTimer === null) return;
    clearTimeout(hostGraceTimer);
    hostGraceTimer = null;
  }

  const onHostLost = (reason: string): void => {
    if (status === 'closed' || status === 'opening') return;
    setStatus('suspended', reason);
    armHostGrace();
  };

  const onHostBack = async (): Promise<void> => {
    if (status !== 'suspended') return;
    try {
      await resync(false);
    } catch (error) {
      report(asS3Error(error));
    }
  };

  // ============================================================
  // 起手：房主
  // ============================================================

  const startHost = (): void => {
    track(
      channel.onRequest(`${ns()}/action`, z.object({ action: rules.schemas.action }), (payload, ctx) => {
        if (!roster.includes(ctx.from)) {
          // 不在这一局里的人不能出牌。**这是房主唯一需要守的边界**
          throw new AccessDeniedError(t('host.session.notInGame', { from: ctx.from }));
        }
        return applyAction((payload as { action: Action }).action, ctx.from);
      }),
    );

    track(
      channel.onRequest(`${ns()}/resync`, z.object({}), (_payload, ctx) => {
        if (!roster.includes(ctx.from) || state === null) {
          throw new AccessDeniedError(t('host.session.notInGame', { from: ctx.from }));
        }
        const view = rules.project(state, ctx.from);
        perPlayerViews.set(ctx.from, view);
        return { seq, view, hash: hashValue(view) };
      }),
    );

    // 「推一次」的另一半：让还没挂上监听的人也能**问**出来（见 ProbeReplySchema）
    track(
      channel.onRequest(SESSION_PROBE_METHOD, z.object({}), () => ({
        open: true as const,
        sessionId,
        host: selfId,
        players: roster,
        options: init.options,
      })),
    );

    track(
      channel.onPeerJoin((id) => {
        // 回来了：先把他标回在线，再给一份全量，别让他猜自己缺了哪几条
        void (async () => {
          if (!roster.includes(id) || state === null) return;
          await applyPresence(id, true);
          if (state === null) return;
          const view = rules.project(state, id);
          perPlayerViews.set(id, view);
          sendView(id, { seq, hash: hashValue(view), view });
          emitPresence();
        })();
      }),
    );

    track(
      channel.onPeerLeave((id) => {
        if (!roster.includes(id)) return;
        void applyPresence(id, false);
        emitPresence();
      }),
    );

    track(
      channel.onPeerState((id, peerState) => {
        if (!roster.includes(id)) return;
        // 心跳判死 / 复活。这是「链路悄悄断了」唯一的发现途径
        void (async () => {
          await applyPresence(id, peerState !== 'down');
          // ★ 复活时补一份全量。断开那段时间推给他的增量无处可去，而他手里的
          //   `lastSeq` 还停在旧值 —— 只靠「下一条增量 + 哈希对不上」得等到
          //   **下一次状态变化**才自愈；若正好轮到他行动，就会干等在那里。
          //   只在 `up` 这一次（`onStateChange` 只在状态真的变化时发）做。
          if (peerState === 'down' || state === null) return;
          const view = rules.project(state, id);
          perPlayerViews.set(id, view);
          sendView(id, { seq, hash: hashValue(view), view });
        })();
      }),
    );

    // ★ 完整状态的**唯一产地**就在这一行
    state = rules.createInitialState({
      sessionId,
      host: selfId,
      players: roster,
      options: init.options,
    });

    channel.notify(SESSION_OPEN_METHOD, {
      sessionId,
      host: selfId,
      players: roster,
      options: init.options,
    });

    pushViews(true);
    setStatus('live');
    emitPresence();
    armHostTimer();
  };

  // ============================================================
  // 起手：客户端
  // ============================================================

  const adopt = (payload: z.infer<typeof OpenPayloadSchema>): void => {
    sessionId = payload.sessionId;
    hostId = payload.host;
    roster = [...payload.players];
    lastSeq = -1;

    // ⚠️ 认下会话之后，把排队等着的应答补注册上（见 `onAsk` 的说明）
    for (const entry of queuedAsks.splice(0)) {
      track(
        channel.onRequest(`${ns()}/${entry.method}`, entry.schema, (payload, ctx) =>
          entry.handler(payload, ctx.from),
        ),
      );
    }

    track(
      channel.onNotify(`${ns()}/view`, ViewPayloadSchema, (raw) => {
        onClientView(raw as { seq: number; hash: string; view?: unknown; patches?: Patch[] });
      }),
    );
    track(
      channel.onNotify(`${ns()}/close`, ClosePayloadSchema, (raw) => {
        close((raw as { reason?: string }).reason ?? t('host.session.hostEnded'));
      }),
    );
    track(
      channel.onNotify(`${ns()}/event`, z.object({ events: z.array(rules.schemas.event) }), (raw) => {
        emitEvents((raw as { events: Event[] }).events);
      }),
    );

    // 认下之后**主动要一次全量**：`open` 只保证"这一局存在"，
    // 而紧跟其后的那条 view 可能在我挂上监听之前就已经过去了
    void resync(false).catch((error: unknown) => report(asS3Error(error)));
  };

  const startClient = (): void => {
    track(
      channel.onNotify(SESSION_OPEN_METHOD, OpenPayloadSchema, (payload) => {
        if (status !== 'opening') return;
        if (init.expectedSessionId !== null && payload.sessionId !== init.expectedSessionId) return;
        adopt(payload);
      }),
    );

    track(channel.onError((error) => report(asS3Error(error))));
    track(
      channel.onPardon((from: string, pardon: PardonReport) => {
        report(new S3Error('HandlerError', t('host.session.pardonReceived', { from, reason: pardon.reason }), pardon));
      }),
    );

    track(
      channel.onPeerLeave((id) => {
        if (id === hostId) onHostLost(t('host.session.hostPeerLeft'));
      }),
    );
    track(
      channel.onPeerJoin((id) => {
        // 房主回来了。**这条路和心跳那条都要有**：
        // 名册上的离开/回归是确定的，而心跳只能发现"链路悄悄断了" ——
        // 少任何一条，都会有一类掉线卡在 `suspended` 里出不来
        if (id === hostId && status === 'suspended') void onHostBack();
      }),
    );
    track(
      channel.onPeerState((id: string, peerState: PeerLiveness) => {
        if (id !== hostId) return;
        if (peerState === 'down') onHostLost(t('host.session.hostWentOffline'));
        else void onHostBack();
      }),
    );

    // 「推一次」+「问一次」。**问必须排在挂完所有监听之后**，
    // 否则两条路都可能在监听就位之前错过（这正是它在真浏览器里被发现的场景）
    void probeOnce();
  };

  /**
   * 朝每个 peer 问一句"这一局开着吗"，取第一个说"开着"的。
   *
   * 客户端在认下之前**不知道房主是谁**（`hostId` 还是 null），所以只能问一圈。
   * ≤5 人的房间里这只是几条请求，而且**只发生一次** —— 这不是轮询。
   */
  const probeOnce = async (): Promise<void> => {
    if (role !== 'client' || status !== 'opening') return;
    const candidates = [...channel.peers()];
    const replies = await Promise.allSettled(
      candidates.map((peerId) => channel.request(peerId, SESSION_PROBE_METHOD, {}, ProbeReplySchema)),
    );
    if (status !== 'opening') return;
    for (const reply of replies) {
      if (reply.status !== 'fulfilled') continue;
      const value = reply.value;
      if (!value.open) continue;
      if (init.expectedSessionId !== null && value.sessionId !== init.expectedSessionId) continue;
      adopt(value);
      return;
    }
  };

  // ============================================================
  // 组装
  // ============================================================

  if (role === 'host') startHost();
  else startClient();

  return {
    get id() {
      return sessionId;
    },
    isHost: role === 'host',
    selfId,
    status: () => status,
    host: () => hostId,
    players: () => roster,
    view: () => currentView,
    stats: () => ({ ...stats }),

    async send(action: Action): Promise<S3Ack> {
      if (status === 'closed') return { ok: false, reason: t('host.session.gameEnded') };
      if (role === 'host') return await applyAction(action, selfId);
      if (hostId === null || status === 'opening') return { ok: false, reason: t('host.session.gameNotStarted') };
      if (status === 'suspended') return { ok: false, reason: t('host.session.hostSuspended') };
      return await channel.request(hostId, `${ns()}/action`, { action }, AckSchema);
    },

    async sync(): Promise<void> {
      if (role === 'host') {
        // 权威在自己手里：不必问谁，重新按人投影、推全量即可
        pushViews(true);
        return;
      }
      // 客户端：重取一份全量（失败会抛，界面据此报「同步失败」）
      await pullFullView();
    },

    onView(handler) {
      viewListeners.add(handler);
      // 晚订阅的人立刻拿到当前视图 —— 界面重建时不必自己再取一次。
      // ⚠️ 于是"订阅"本身就会触发一次回调，写序列断言时要记得这一点
      if (currentView !== null) notifySafely([handler], currentView, t('host.session.viewLabel'));
      return () => viewListeners.delete(handler);
    },
    onEvent(handler) {
      eventListeners.add(handler);
      return () => eventListeners.delete(handler);
    },
    onStatus(handler) {
      statusListeners.add(handler);
      return () => statusListeners.delete(handler);
    },
    onError(handler) {
      errorListeners.add(handler);
      return () => errorListeners.delete(handler);
    },
    onPresence(handler) {
      presenceListeners.add(handler);
      return () => presenceListeners.delete(handler);
    },
    onAsk(method, schema, handler) {
      /**
       * ⚠️ **客户端在认下会话之前，`ns()` 还是空的。**
       *
       * 时序是这样的：房主广播 `open` → 客户端建 ClientSession → 界面
       * 立刻挂上去注册应答。而 `sessionId` 要等 `open` **送到**才有，
       * 所以这时候注册的话，方法名会变成 `s3/session//steal-order` ——
       * 房主问的是 `s3/session/<真 id>/steal-order`，于是对端报
       * `BadMethod`（"方法没注册"），而**两边看起来都没错**。
       *
       * 所以：认得下就现在注册，认不下就先排队，`adopt` 时补上。
       */
      if (sessionId === '') {
        queuedAsks.push({
          method,
          schema,
          handler: handler as (payload: unknown, from: string) => unknown,
        });
        return () => {
          const at = queuedAsks.findIndex((entry) => entry.method === method);
          if (at >= 0) queuedAsks.splice(at, 1);
        };
      }
      const off = channel.onRequest(`${ns()}/${method}`, schema, (payload, ctx) =>
        handler(payload, ctx.from),
      );
      track(off);
      return off;
    },
    ask(to, method, payload, schema, timeoutMs) {
      return channel.request(to, `${ns()}/${method}`, payload, schema, timeoutMs);
    },
    close,
    debug,
  };
}

// ============================================================
// 入口
// ============================================================

export function hostSession<State, View, Action, Event>(
  channel: S3Channel,
  rules: S3Rules<State, View, Action, Event>,
  options: HostSessionOptions,
): S3Session<View, Action, Event> {
  const sessionId = options.sessionId ?? makeSessionId();
  return createSession({
    channel,
    rules,
    role: 'host',
    sessionId,
    expectedSessionId: sessionId,
    players: options.players,
    options: options.options ?? null,
    hostGraceMs: DEFAULT_HOST_GRACE_MS,
    bots: options.bots ?? [],
  });
}

export function clientSession<State, View, Action, Event>(
  channel: S3Channel,
  rules: S3Rules<State, View, Action, Event>,
  options: ClientSessionOptions = {},
): S3Session<View, Action, Event> {
  return createSession({
    channel,
    rules,
    role: 'client',
    sessionId: '',
    expectedSessionId: options.sessionId ?? null,
    players: [],
    options: null,
    hostGraceMs: options.hostGraceMs ?? DEFAULT_HOST_GRACE_MS,
  });
}
